import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Loader2, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/context/AuthContext';
import { apiErrorMessage } from '@/lib/api';
import { isPasswordStrongEnough } from '@/lib/passwordStrength';
import { SIGNUP_CLIENT_PAGE } from '@/constants/testIds';

export default function SignupClient() {
  const { signupAsClient } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const em = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em)) return "That email doesn't look right — check for typos (e.g. name@example.com).";
    if (!isPasswordStrongEnough(password)) return 'Please meet all the password requirements below.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const problem = validate();
    if (problem) { setError(problem); return; }
    setLoading(true);
    try {
      await signupAsClient(email.trim(), password, name.trim());
      navigate('/client');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create your account. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[#0A0A0A] px-4 py-16" data-testid={SIGNUP_CLIENT_PAGE.root}>
      <SEO title="Create Your Account" description="Create a client account to view your LXRA Studios gallery." />
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </Link>

        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8 sm:p-10">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] mb-5">
            <UserPlus className="h-5 w-5" />
          </span>
          <h1 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] text-center">Create Your Account</h1>
          <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] text-center">
            If your photographer has already added your email, your galleries will be linked automatically.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="signup-name" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Name</Label>
              <Input
                id="signup-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid={SIGNUP_CLIENT_PAGE.nameInput}
                className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 focus-visible:ring-[hsl(var(--lxra-gold))]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-email" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Email</Label>
              <Input
                id="signup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={SIGNUP_CLIENT_PAGE.emailInput}
                className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 focus-visible:ring-[hsl(var(--lxra-gold))]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-password" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Password</Label>
              <Input
                id="signup-password"
                type="password"
                required
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={SIGNUP_CLIENT_PAGE.passwordInput}
                className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 focus-visible:ring-[hsl(var(--lxra-gold))]"
              />
              <PasswordStrengthMeter password={password} testIdPrefix={SIGNUP_CLIENT_PAGE.passwordInput} />
            </div>

            {error && (
              <p className="text-xs text-red-400" data-testid={SIGNUP_CLIENT_PAGE.errorText}>{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid={SIGNUP_CLIENT_PAGE.submitButton}
              className="w-full h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-[hsl(var(--lxra-muted))]">
            Already have an account?{' '}
            <Link to="/login" data-testid={SIGNUP_CLIENT_PAGE.loginLink} className="text-[hsl(var(--lxra-gold))] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
